import { useState, useEffect, useMemo } from "react";
import { useApp } from "../context/AppContext";

// Прогноз трафіку: «якщо підняти позиції всієї семантики на X%» → скільки переходів/міс
// і який % ключів буде в топ-1/3/5/10. Модель: частотність × власна CTR-крива з GSC.
export default function TrafficForecastView() {
  const { proj, activeId, apiFetch, t, card, btnP, dark } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [improve, setImprove] = useState(20); // % покращення позицій

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    apiFetch(`/api/projects/${activeId}/forecast`).then(r => r.json())
      .then(d => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [activeId, apiFetch]);

  const calc = useMemo(() => {
    if (!data || !data.hist) return null;
    const curve = data.curve || {};
    const ctrAt = (p) => (p >= 1 && p <= 10 ? (curve[p] || 0) : 0);
    const newPos = (p) => Math.max(1, Math.round(p * (1 - improve / 100)));
    let curClicks = 0, fcClicks = 0;
    const curTop = { 1: 0, 3: 0, 5: 0, 10: 0 }, fcTop = { 1: 0, 3: 0, 5: 0, 10: 0 };
    for (const p in data.hist) {
      const P = +p; const { count, freq } = data.hist[p];
      curClicks += freq * ctrAt(P);
      const np = newPos(P);
      fcClicks += freq * ctrAt(np);
      for (const k of [1, 3, 5, 10]) { if (P <= k) curTop[k] += count; if (np <= k) fcTop[k] += count; }
    }
    const total = data.totalKeywords || 1;
    const pct = (n) => Math.round(n / total * 100);
    const ratio = curClicks > 0 ? fcClicks / curClicks : 1;
    const base = data.actualClicks > 0 ? data.actualClicks : curClicks; // baseline = реальні кліки GSC
    const showCur = Math.round(base), showFc = Math.round(base * ratio);
    return {
      curClicks: showCur, fcClicks: showFc,
      delta: showCur > 0 ? Math.round((showFc - showCur) / showCur * 100) : null,
      addClicks: showFc - showCur, basedOnGsc: data.actualClicks > 0,
      curTop, fcTop, pct,
    };
  }, [data, improve]);

  if (!proj) return null;
  const fmt = (n) => Number(n || 0).toLocaleString("uk-UA");

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Прогноз трафіку</h2>
        <span style={{ fontSize: 12, color: t.textMuted }}>{proj.domain}</span>
      </div>

      {loading ? (
        <div style={{ ...card, textAlign: "center", padding: 40, color: t.textMuted }}>Рахуємо модель…</div>
      ) : !data || !data.totalKeywords ? (
        <div style={{ ...card, textAlign: "center", padding: 40 }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>📈</div>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>Недостатньо даних</p>
          <p style={{ fontSize: 13, color: t.textMuted }}>Потрібна семантика (частотність ключів) {data && !data.hasGsc ? "і підключений GSC (для CTR-кривої та поточних позицій)" : ""}.</p>
        </div>
      ) : (
        <>
          {/* Калькулятор */}
          <div style={{ ...card, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Якщо підняти позиції всієї семантики на</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: "#6366f1", minWidth: 60 }}>{improve}%</span>
            </div>
            <input type="range" min={0} max={90} step={5} value={improve} onChange={e => setImprove(+e.target.value)}
              style={{ width: "100%", accentColor: "#6366f1" }} />
            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4 }}>
              Модель: нова позиція = поточна × (1 − {improve}%). {fmt(data.totalKeywords)} ключів ({data.ranked} вже ранжуються, решта рахуються як позиція 100).
              CTR-крива: {data.ownCurve ? "власна з GSC" : "частково загальна (мало даних GSC)"}. Базис «зараз»: {calc && calc.basedOnGsc ? "фактичні кліки GSC" : "модель"}.
            </div>
          </div>

          {/* Великі цифри */}
          {calc && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 14 }}>
              <Tile t={t} card={card} label="Зараз, переходів/міс" value={fmt(calc.curClicks)} color={t.text} />
              <Tile t={t} card={card} label="Прогноз, переходів/міс" value={fmt(calc.fcClicks)} color="#10b981" />
              <Tile t={t} card={card} label="Приріст" value={(calc.delta != null ? `+${calc.delta}%` : "—")} sub={calc.addClicks > 0 ? `+${fmt(calc.addClicks)}/міс` : ""} color="#6366f1" />
            </div>
          )}

          {/* Розподіл по топах: зараз → прогноз */}
          {calc && (
            <div style={{ ...card, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Розподіл ключів по топах</div>
              {[["топ-1", 1], ["топ-3", 3], ["топ-5", 5], ["топ-10", 10]].map(([label, k]) => {
                const cur = calc.pct(calc.curTop[k]), fc = calc.pct(calc.fcTop[k]);
                return (
                  <div key={k} style={{ display: "flex", alignItems: "center", gap: 12, padding: "7px 0", borderTop: k !== 1 ? `1px solid ${dark ? "#1e1e2c" : "#f0f0f0"}` : "none" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, width: 56 }}>{label}</span>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", position: "relative", overflow: "hidden" }}>
                      <div style={{ position: "absolute", inset: 0, width: `${cur}%`, background: t.textMuted, opacity: 0.5 }} />
                      <div style={{ position: "absolute", inset: 0, width: `${fc}%`, background: "#10b981", transition: "width .3s" }} />
                    </div>
                    <span style={{ fontSize: 12, width: 120, textAlign: "right", color: t.textMuted }}>
                      {cur}% <span style={{ color: t.text }}>→ {fc}%</span> <span style={{ color: "#10b981", fontWeight: 700 }}>({calc.fcTop[k] - calc.curTop[k] >= 0 ? "+" : ""}{calc.pct(calc.fcTop[k]) - calc.pct(calc.curTop[k])}пп)</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* CTR-крива проекту */}
          <div style={{ ...card }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>CTR-крива проекту {data.curveSource === "year" ? "(власна, середня за рік з GSC)" : data.ownCurve ? "(власна, з GSC)" : "(частково загальна)"}</div>
            <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12 }}>Який % показів стає переходами залежно від позиції — звідси і рахуємо прогноз.</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 120 }}>
              {Array.from({ length: 10 }, (_, i) => i + 1).map(p => {
                const v = (data.curve || {})[p] || 0; const maxV = Math.max(...Object.values(data.curve || { 1: 0.3 }));
                return (
                  <div key={p} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                    <div style={{ fontSize: 9, color: t.textMuted }}>{(v * 100).toFixed(1)}%</div>
                    <div style={{ width: "100%", height: `${maxV ? (v / maxV) * 90 : 0}px`, minHeight: 2, background: "linear-gradient(180deg,#6366f1,#8b5cf6)", borderRadius: "4px 4px 0 0" }} />
                    <div style={{ fontSize: 10, color: t.textMuted }}>{p}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 14, lineHeight: 1.5 }}>
              ⚠️ Оцінка: реальний CTR залежить від SERP-фіч (AI Overviews, реклама, сніпети) та сезонності. Цифри орієнтовні.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ t, card, label, value, sub, color }) {
  return (
    <div style={{ ...card, textAlign: "center", padding: 18 }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: "#10b981", fontWeight: 600, marginTop: 2 }}>{sub}</div> : null}
      <div style={{ fontSize: 10.5, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", marginTop: 6 }}>{label}</div>
    </div>
  );
}
